import React, { useEffect, useMemo, useState } from "react";
import { Devices } from "@app/shared/src/api/api.gen";
import { ChirpConfig, Device, FleetDefaults, TrackerConfig, TrackerConfigType } from "@app/shared/src/models";
import { Card, CardNote, CardHead, FRow, Note, Select, Spinner, useToast } from "@app/shared/src/components_v2";
import {
    RETENTION_DAY_CHOICES,
    RadarVideoSettings,
    STREAM_PROFILES,
    VIDEO_PROFILES,
    applyRadarVideo,
    matchProfile,
    pushableRadarVideoKeys,
    radarVideoFromDefaults,
    readRadarVideo,
    trackersOfType,
} from "../../helpers/radarVideoSettings";
import { ApplyToFleetDialog } from "../shared/ApplyToFleetDialog";
import { SaveBar } from "../shared/SaveBar";
import { Toggle } from "../shared/Toggle";

/**
 * The organization's stated radar and video defaults.
 *
 * This edits the stored record rather than inferring a default from whatever the trucks happen to
 * hold — inference reads "Mixed" the moment one unit drifts, which is exactly when a director most
 * needs to know the intended value.
 *
 * Saving states the intent and then pushes it, but only onto units that were following the *old*
 * value. A truck someone deliberately moved keeps its setting, which is what makes this safe to
 * press; the confirmation names how many will change before anything is written.
 */

type FieldKey = keyof RadarVideoSettings;

const FIELD_LABELS: Partial<Record<FieldKey, string>> = {
    radarEnabled: "Radar transmitter",
    chirpConfigId: "Radar profile",
    radarTrackerConfigId: "Radar tracker",
    videoTrackerConfigId: "Camera tracker",
    dvrRecordingEnabled: "DVR recording",
    dvrResolutionX: "Recording profile",
    dvrRetentionHours: "Retention",
    streamResolutionX: "Streaming profile",
};

/** Profile fields move together, so one label stands for the group. */
const GROUPED: Partial<Record<FieldKey, FieldKey>> = {
    dvrResolutionY: "dvrResolutionX",
    dvrFps: "dvrResolutionX",
    dvrBitrateBps: "dvrResolutionX",
    streamResolutionY: "streamResolutionX",
    streamFps: "streamResolutionX",
    streamBitrateBps: "streamResolutionX",
};

export const FleetRadarVideoSection: React.FC<{
    defaults?: FleetDefaults;
    devices: Device[];
    /**
     * Complete device records from useFullDevices — the page fetches one set for every section.
     * The roster copies in `devices` omit the sensor config (the org list travels light), so judged
     * off them no truck ever "follows" a radar preset — radar changes silently pushed to nobody —
     * and applyRadarVideo could not write the radar half of what it did push.
     */
    fullDevices?: Device[];
    unreachable: number;
    reloadFullDevices: () => Promise<void>;
    chirpConfigs: ChirpConfig[];
    trackerConfigs: TrackerConfig[];
    onSave: (apply: (target: FleetDefaults) => void) => Promise<void>;
    onPushed: () => void;
}> = ({ defaults, devices, fullDevices, unreachable, reloadFullDevices, chirpConfigs, trackerConfigs, onSave, onPushed }) => {
    const toast = useToast();
    const saved = useMemo(() => (defaults ? radarVideoFromDefaults(defaults) : undefined), [defaults]);
    const [draft, setDraft] = useState<RadarVideoSettings | undefined>(saved);
    const [pending, setPending] = useState<{ targets: Device[]; run: () => Promise<void>; summary: string }>();
    const [busy, setBusy] = useState(false);

    useEffect(() => setDraft(saved), [saved]);

    if (!draft || !saved || !defaults) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    const set = <K extends FieldKey>(key: K, value: RadarVideoSettings[K]) => setDraft({ ...draft, [key]: value });
    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

    const changedKeys = (Object.keys(saved) as FieldKey[]).filter((k) => draft[k] !== saved[k]);
    const changedLabels = Array.from(
        new Set(changedKeys.map((k) => FIELD_LABELS[GROUPED[k] ?? k] ?? k).filter(Boolean)),
    );
    /*
     * The subset of changed keys that can reach a truck at all. A preset moving off "no fleet-wide
     * choice" has no followers — an unset id on a unit means "commissioned with its own" (or a
     * custom tracker), not "on the default" — and one moving TO no-choice writes nothing. Judging
     * targets off changedKeys matched undefined against undefined, so the first fleet-wide preset
     * choice prompted to rewrite every blank and custom unit in the fleet.
     */
    const pushKeys = pushableRadarVideoKeys(saved, draft);

    const radarTrackers = trackersOfType(trackerConfigs, TrackerConfigType.Radar);
    const videoTrackers = trackersOfType(trackerConfigs, TrackerConfigType.Video);

    /**
     * Units still on the old value for at least one thing that changed.
     *
     * Per field rather than per unit: changing only retention should move the trucks that were on
     * the old retention, not just those identical in every respect.
     */
    const targets = (fullDevices ?? []).filter((device) => {
        const current = readRadarVideo(device);
        return pushKeys.some((k) => current[k] === saved[k]);
    });

    const run = async () => {
        setBusy(true);
        try {
            await onSave((target) => {
                target.radarEnabled = draft.radarEnabled ?? target.radarEnabled;
                target.chirpConfigId = draft.chirpConfigId;
                target.radarTrackerConfigId = draft.radarTrackerConfigId;
                target.videoTrackerConfigId = draft.videoTrackerConfigId;
                target.dvrRecordingEnabled = draft.dvrRecordingEnabled;
                target.dvrResolutionX = draft.dvrResolutionX;
                target.dvrResolutionY = draft.dvrResolutionY;
                target.dvrFps = draft.dvrFps;
                target.dvrBitrateBps = draft.dvrBitrateBps;
                target.dvrRetentionHours = draft.dvrRetentionHours;
                target.streamResolutionX = draft.streamResolutionX;
                target.streamResolutionY = draft.streamResolutionY;
                target.streamFps = draft.streamFps;
                target.streamBitrateBps = draft.streamBitrateBps;
            });

            let pushed = 0;
            for (const device of targets) {
                const current = readRadarVideo(device);
                // Only the fields this unit was actually following — anything it had been
                // moved off deliberately stays where it is. pushKeys, not changedKeys: a
                // preset the fleet had never chosen matches every blank or custom unit as
                // undefined === undefined, and writing it here is what clobbered them.
                const next = { ...current };
                for (const k of pushKeys) {
                    if (current[k] === saved[k]) (next as Record<string, unknown>)[k] = draft[k];
                }
                applyRadarVideo(device, next, { chirpConfigs, trackerConfigs });
                await Devices.save(device);
                pushed += 1;
            }

            toast(
                (pushed > 0
                    ? `Defaults saved and pushed to ${pushed} unit${pushed === 1 ? "" : "s"}`
                    : "Fleet defaults saved") +
                    (unreachable > 0
                        ? `. ${unreachable} unit${unreachable === 1 ? "" : "s"} could not be loaded and ${
                              unreachable === 1 ? "was" : "were"
                          } skipped.`
                        : ""),
            );
            onPushed();
            // The copies just written are stale by exactly what was written; the next
            // save's target list has to be judged against what the trucks now hold.
            await reloadFullDevices();
        } finally {
            setBusy(false);
            setPending(undefined);
        }
    };

    const commit = () => {
        // Nothing reaches a truck — only the stated default moves (typically a preset picked for
        // the first time, or a choice withdrawn). Confirming a fleet-wide push that is not
        // happening would train directors to click through the one dialog that matters — the same
        // rule the alerts section applies.
        if (pushKeys.length === 0) {
            void run();
            return;
        }
        setPending({ targets, summary: changedLabels.join(" · "), run });
    };

    const recording = matchProfile(VIDEO_PROFILES, draft.dvrResolutionX, draft.dvrResolutionY, draft.dvrFps);
    const streaming = matchProfile(STREAM_PROFILES, draft.streamResolutionX, draft.streamResolutionY, draft.streamFps);

    /**
     * A stored choice that is not in the offered list — a profile of the wrong type picked before
     * the lists were split by type, or one since deleted. Shown disabled so the select cannot
     * silently display "No fleet-wide choice" over a value that is actually set.
     */
    const strayChoice = (id: string | undefined, options: TrackerConfig[], kind: "radar" | "camera") => {
        if (id === undefined || options.some((t) => t.id === id)) return null;
        const name = trackerConfigs.find((t) => t.id === id)?.name;
        return (
            <option value={id} disabled>
                {name ? `${name} · not a ${kind} tracker` : "Unknown profile"}
            </option>
        );
    };

    return (
        <>
            <Card>
                <CardHead title="Radar" note="what every unit should run" />
                <div className="mt-2">
                    <FRow title="Radar enabled" sub="The transmitter itself — off means a truck detects nothing">
                        <Toggle on={!!draft.radarEnabled} onChange={(v) => set("radarEnabled", v)} label="Radar enabled" />
                    </FRow>
                    <FRow stack title="Radar profile" sub="Chirp configuration">
                        <Select
                            value={draft.chirpConfigId ?? ""}
                            onChange={(e) => set("chirpConfigId", e.target.value || undefined)}
                        >
                            <option value="">No fleet-wide choice</option>
                            {chirpConfigs.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Select>
                    </FRow>
                    <FRow stack title="Radar tracker">
                        <Select
                            value={draft.radarTrackerConfigId ?? ""}
                            aria-label="Fleet radar tracker profile"
                            onChange={(e) => set("radarTrackerConfigId", e.target.value || undefined)}
                        >
                            <option value="">No fleet-wide choice</option>
                            {strayChoice(draft.radarTrackerConfigId, radarTrackers, "radar")}
                            {radarTrackers.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </Select>
                    </FRow>
                    <FRow stack title="Camera tracker">
                        <Select
                            value={draft.videoTrackerConfigId ?? ""}
                            aria-label="Fleet camera tracker profile"
                            onChange={(e) => set("videoTrackerConfigId", e.target.value || undefined)}
                        >
                            <option value="">No fleet-wide choice</option>
                            {strayChoice(draft.videoTrackerConfigId, videoTrackers, "camera")}
                            {videoTrackers.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </Select>
                    </FRow>
                </div>
                <CardNote>
                    A preset left at "no fleet-wide choice" is not compared against, so a truck commissioned with its own is
                    not reported as overridden until someone actually picks one here. Picking one moves only units already
                    following a previously stated choice — trucks running their own, including a custom tracker, are left
                    alone.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Video" note="recording and streaming" />
                <div className="mt-2">
                    <FRow title="DVR recording" sub="Continuous recording to each unit's disk">
                        <Toggle
                            on={draft.dvrRecordingEnabled}
                            onChange={(v) => set("dvrRecordingEnabled", v)}
                            label="DVR recording"
                        />
                    </FRow>
                    <FRow stack title="Recording profile" sub="Higher quality fills the disk sooner">
                        <Select
                            value={recording?.label ?? ""}
                            onChange={(e) => {
                                const p = VIDEO_PROFILES.find((x) => x.label === e.target.value);
                                if (!p) return;
                                setDraft({
                                    ...draft,
                                    dvrResolutionX: p.width,
                                    dvrResolutionY: p.height,
                                    dvrFps: p.fps,
                                    dvrBitrateBps: p.bitrateBps,
                                });
                            }}
                        >
                            {!recording && (
                                <option value="">
                                    Custom · {draft.dvrResolutionX}×{draft.dvrResolutionY} · {draft.dvrFps} fps
                                </option>
                            )}
                            {VIDEO_PROFILES.map((p) => (
                                <option key={p.label} value={p.label}>
                                    {p.label}
                                </option>
                            ))}
                        </Select>
                    </FRow>
                    <FRow title="Retention" sub="A full disk overwrites the oldest clips first, so this is a floor">
                        <Select
                            value={String(Math.round(draft.dvrRetentionHours / 24))}
                            onChange={(e) => set("dvrRetentionHours", Number(e.target.value) * 24)}
                        >
                            {RETENTION_DAY_CHOICES.map((d) => (
                                <option key={d} value={d}>
                                    {d} days
                                </option>
                            ))}
                        </Select>
                    </FRow>
                    <FRow stack title="Streaming profile" sub="Lower uses less of each truck's data plan">
                        <Select
                            value={streaming?.label ?? ""}
                            onChange={(e) => {
                                const p = STREAM_PROFILES.find((x) => x.label === e.target.value);
                                if (!p) return;
                                setDraft({
                                    ...draft,
                                    streamResolutionX: p.width,
                                    streamResolutionY: p.height,
                                    streamFps: p.fps,
                                    streamBitrateBps: p.bitrateBps,
                                });
                            }}
                        >
                            {!streaming && (
                                <option value="">
                                    Custom · {draft.streamResolutionX}×{draft.streamResolutionY} · {draft.streamFps} fps
                                </option>
                            )}
                            {STREAM_PROFILES.map((p) => (
                                <option key={p.label} value={p.label}>
                                    {p.label}
                                </option>
                            ))}
                        </Select>
                    </FRow>
                </div>
                <CardNote>Changes reach each unit on its next heartbeat.</CardNote>
            </Card>

            {unreachable > 0 && (
                <Note tone="warn">
                    {unreachable} unit{unreachable === 1 ? "" : "s"} could not be loaded, so a save now would state the
                    default without reaching {unreachable === 1 ? "it" : "them"}.
                </Note>
            )}

            <SaveBar
                show={dirty}
                busy={busy}
                /* Until every unit's full record is in hand the target list would be wrong, and a
                   push judged off it would quietly skip trucks. */
                canSave={fullDevices !== undefined}
                label={fullDevices ? "Unsaved · fleet defaults" : "Loading unit records…"}
                onSave={commit}
                onDiscard={() => setDraft(saved)}
            />

            {pending && (
                <ApplyToFleetDialog
                    open
                    onClose={() => setPending(undefined)}
                    title="Save radar & video defaults"
                    summary={pending.summary}
                    targets={pending.targets}
                    /* By id — the targets are the full copies, not the roster objects. */
                    untouched={devices.filter((d) => !pending.targets.some((t) => t.id === d.id))}
                    onConfirm={pending.run}
                />
            )}
        </>
    );
};

